import { useCallback, useEffect, useState } from 'react';
import type { Channel } from '@shared/types.ts';
import { useChannels, useGroups, type ChannelFilter } from '@/lib/queries.ts';
import { player } from '@/lib/player.ts';
import { usePlayer } from '@/stores/playback.ts';
import { ChannelCard } from './ChannelCard.tsx';
import { useTuneChannel } from './useTuneChannel.ts';
import { SourceBusyDialog } from './SourceBusyDialog.tsx';
import { ChannelMenu } from './ChannelMenu.tsx';
import { pushKeyLayer, type NavKey } from '@/lib/keys.ts';

export function ChannelGrid({ kind }: { kind: 'tv' | 'radio' }) {
  const [filter, setFilter] = useState<ChannelFilter>({});
  const { data: channels, isLoading, error } = useChannels(kind, filter);
  const { data: groupData } = useGroups(kind);
  const { channel: current } = usePlayer();
  const [menuFor, setMenuFor] = useState<string | null>(null);
  /* The grid you are looking at IS the list left/right cycles through, which
     is why the lineup is handed in rather than fetched by the hook. */
  const { tune, busy, listenInstead, dismissBusy } = useTuneChannel(channels);

  /**
   * MENU acts on whatever is focused.
   *
   * Read from the DOM rather than from React state on purpose: the grid's
   * focus lives in the DOM (see ui/Focusable.tsx), and keeping a mirrored
   * copy in state purely so this handler could read it would re-render the
   * whole grid on every press of the remote — the exact cost that model
   * exists to avoid.
   */
  useEffect(() => pushKeyLayer((key: NavKey) => {
    if (key !== 'menu') return false;
    const id = (document.activeElement as HTMLElement | null)
      ?.getAttribute('data-nav')?.replace(/^ch-/, '');
    // '' opens the menu with no channel in it — MENU also carries the lineup
    // filter now, so it has to do something even when focus is not on a card
    // (an empty group, say). A context key that silently does nothing is
    // indistinguishable from a broken one.
    setMenuFor(id && (channels ?? []).some((c) => c.id === id) ? id : '');
    return true;
  }), [channels]);


  /**
   * The only trace the filter leaves on screen, and deliberately not
   * focusable — it is a caption, not a control.
   *
   * Filtering moved into the MENU dialog to get a whole focus lane off the
   * grid (see ChannelMenu). The cost of hiding a control is that people
   * forget it exists, and the specific way that bites is someone filtering to
   * a group, coming back an hour later, and concluding half their channels
   * are gone. So whenever the grid is showing less than everything, it says
   * what it is showing and which key changes it. At "All" it says nothing,
   * because a caption that is always there is a caption nobody reads.
   */
  const caption = filter.favourites ? '★ Favorites'
    : filter.hidden ? 'Hidden channels'
    : filter.group ?? null;

  const banner = caption && (
    <div className="lineup-caption">
      <span className="lineup-name">{caption}</span>
      <span className="muted">MENU to change</span>
    </div>
  );

  if (isLoading) return <>{banner}<div className="grid">{skeletons(12)}</div></>;
  if (error) return <>{banner}<div className="muted center">Could not load channels.</div></>;

  const menuChannel = (channels ?? []).find((c) => c.id === menuFor) ?? null;

  return (
    <>
      {banner}

      {(channels ?? []).length === 0 && (
        <div className="muted center">
          {filter.favourites
            ? 'No favorites yet — press MENU on a channel to star it.'
            : filter.hidden
              ? 'Nothing hidden.'
              : 'No channels in this group — press MENU to change what is shown.'}
        </div>
      )}

      <div className="grid">
        {(channels ?? []).map((c, i) => (
          <ChannelCard
            key={c.id}
            channel={c}
            onPlay={tune}
            isPlaying={current?.id === c.id}
            favourite={c.favourite}
            // Give the remote somewhere to start. Without an initially
            // focused element the first D-pad press has no origin.
            autoFocus={i === 0}
          />
        ))}
      </div>

      {menuFor !== null && (
        <ChannelMenu
          channel={menuChannel}
          groups={groupData?.groups ?? []}
          favourites={groupData?.favourites ?? 0}
          hidden={groupData?.hidden ?? 0}
          filter={filter}
          onFilter={setFilter}
          onClose={() => setMenuFor(null)}
        />
      )}

      {busy && (
        <SourceBusyDialog
          active={busy.active}
          onListen={listenInstead}
          onCancel={dismissBusy}
        />
      )}
    </>
  );
}

/** Skeletons rather than a spinner — the layout never jumps. */
function skeletons(n: number) {
  return Array.from({ length: n }, (_, i) => (
    <div key={i} className="card skeleton">
      <div className="logo" /><div className="meta"><div className="title" /></div>
    </div>
  ));
}
